import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi } from "../Helpers";
import { FiClock, FiCheckCircle, FiXCircle } from "react-icons/fi";

export default function OneApplication({ data }) {
  const appContext = useContext(AppContext);

  /* Component Level Local States */
  const [jobTitle, setJobTitle] = useState(data.jobtitle || data.jobTitle || null);
  const [fetchingTitle, setFetchingTitle] = useState(false);

  const applicationUuid = data.uuid; 
  const rawStatus = (data.status || "submitted").toLowerCase();

  // Handle contextual UI layout state boundaries cleanly
  let badgeText = "Submitted";
  let statusClass = "bg-info-soft text-info";
  let statusIcon = <FiClock size={14} />;

  if (rawStatus === "under-review" || rawStatus === "review") {
    badgeText = "In Review";
    statusClass = "bg-warning-soft text-warning";
    statusIcon = <FiClock size={14} fill="currentColor" />;
  } else if (rawStatus === "accepted" || rawStatus === "approved") {
    badgeText = "Accepted";
    statusClass = "bg-success-soft text-success";
    statusIcon = <FiCheckCircle size={14} />;
  } else if (rawStatus === "rejected" || rawStatus === "declined") {
    badgeText = "Declined";
    statusClass = "bg-danger-soft text-danger";
    statusIcon = <FiXCircle size={14} />;
  }

  /* Background Workaround Fetcher */
  async function fetchMissingJobTitle() {
    if (jobTitle) return; 

    setFetchingTitle(true);
    await callApi('jobapplicant/get-job-application-data', { jobApplicationUuid: applicationUuid })
      .then((response) => {
        if (response && response.status === 1 && response.data) {
          setJobTitle(response.data.jobTitle || response.data.jobtitle || "Position Details");
        } else {
          setJobTitle("View Position Status");
        }
      })
      .catch(() => {
        setJobTitle("View Position Status");
      });
    setFetchingTitle(false);
  }

  useEffect(() => {
    if (applicationUuid) {
      fetchMissingJobTitle();
    }
  }, [applicationUuid]);

  return (
    <>
      <style>{`
        :root {
          --clinical-teal: #0d9488;
          --navy-900: #0f172a;
          --slate-50: #f8fafc;
          --slate-200: #e2e8f0;
          --slate-500: #64748b;
        }

        .one-app-card {
          width: 100%;
          min-height: 82px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 14px 16px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid var(--slate-200);
          transition: all 0.2s ease;
          cursor: pointer;
        }
        @media (min-width: 576px) {
          .one-app-card {
            padding: 20px;
            min-height: 94px;
          }
        }
        .one-app-card:hover {
          border-color: var(--clinical-teal);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
          transform: translateY(-1px);
        }

        .app-title-text {
          font-size: 0.95rem;
          color: var(--navy-900);
          margin: 0 0 4px 0;
          line-height: 1.3;
        }
        @media (min-width: 576px) {
          .app-title-text {
            font-size: 1rem;
            margin-bottom: 8px;
          }
        }

        .app-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.65rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          white-space: nowrap;
          flex-shrink: 0;
        }
        @media (min-width: 576px) {
          .app-badge {
            gap: 6px;
            padding: 6px 12px;
            font-size: 0.75rem;
          }
        }

        .bg-info-soft { background: #e0f2fe; color: #0369a1; }
        .bg-warning-soft { background: #fef3c7; color: #d97706; }
        .bg-success-soft { background: #dcfce7; color: #166534; }
        .bg-danger-soft { background: #fee2e2; color: #991b1b; }
        
        .loading-shimmer {
          display: inline-block;
          height: 16px;
          width: 120px;
          background: #f1f5f9;
          border-radius: 4px;
          animation: pulse 1.5s infinite ease-in-out;
        }
        @media (min-width: 576px) {
          .loading-shimmer { width: 160px; }
        }
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
      `}</style>

      <div className="one-app-card" onClick={() => {
        appContext.navTo({
          item: 'side-view',
          subItem: 'job-options', 
          extraItem: data.jobUuid || data.jobuuid
        });
      }}>
        <div className="d-flex justify-content-between align-items-center gap-2 gap-sm-3">
          <div style={{ minWidth: 0, flex: 1 }}>
            {fetchingTitle ? (
              <div className="loading-shimmer"></div>
            ) : (
              <h6 className="fw-bold app-title-text text-truncate">
                {jobTitle}
              </h6>
            )}
            <div className="text-muted small text-truncate">
              Ref ID: <span className="fw-mono text-dark">{applicationUuid?.substring(0, 8)}</span>
            </div>
          </div>

          {/* Changed data.status to badgeText to render styled label options instead of raw db strings */}
          <span className={`app-badge ${statusClass}`}>
            {statusIcon} {data.status}
          </span>
        </div>
      </div>
    </>
  );
}